import { Component, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigurableDialog, DialogModule, DialogService } from '@proget-shared/dialog';
import { CalendarModule } from '@proget-shared/form/calendar';
import { CheckboxModule } from '@proget-shared/form/checkbox';
import { ConditionalFormGeneratorService } from '@proget-shared/form/conditional-form-generator';
import { CustomValidators } from '@proget-shared/form/custom-validators';
import { FormControlErrorModule } from '@proget-shared/form/form-control-error';
import { FormHelperModule } from '@proget-shared/form/helper';
import { DisplayFormErrors } from '@proget-shared/grid/grid-actions';
import { ResponseErrorForm } from '@proget-shared/helper/response-error';
import { TranslateModule } from '@proget-shared/translate';

import { REPORT_TYPE } from '../../const';

@Component({
  selector: 'lib-user-card-generate-report',
  templateUrl: './generate-report.component.html',
  styleUrls: ['./generate-report.component.scss'],
  providers: [ConditionalFormGeneratorService],
  imports: [
    ReactiveFormsModule,
    DialogModule,
    CheckboxModule,
    CalendarModule,
    FormControlErrorModule,
    FormHelperModule,
    TranslateModule,
  ],
})
export class GenerateReportComponent implements DisplayFormErrors, ConfigurableDialog {
  protected readonly maxDate = new Date();

  protected minDate: Date = null;
  protected errors: ResponseErrorForm = {};
  protected form: FormGroup;

  private readonly conditionalFormGeneratorService = inject(ConditionalFormGeneratorService);
  private readonly dialog = inject(DialogService);

  constructor() {
    this.createForm();
  }

  onConfiguration(configuration: any): void {
    const dateString: string = this.getMinDateString(configuration);
    const date: Date = dateString ? new Date(dateString) : null;

    this.minDate = date && !isNaN(date.getTime()) ? date : null;
  }

  setFormErrors(formErrors: ResponseErrorForm): void {
    this.errors = formErrors;
  }

  clearFormErrors(): void {
    this.errors = {};
  }

  protected close(): void {
    this.dialog.reject(this);
  }

  protected submit(): void {
    if (this.form.invalid) {
      return;
    }

    const { foreground, mobile, network, storage } = this.form.value;
    const output = { foreground, mobile, network, storage } as any;

    if (foreground) {
      output.foregroundFrom = this.form.value.foregroundRange.from;
      output.foregroundTo = this.form.value.foregroundRange.to;
    }

    if (mobile) {
      output.mobileFrom = this.form.value.mobileRange.from;
      output.mobileTo = this.form.value.mobileRange.to;
    }

    if (network) {
      output.networkFrom = this.form.value.networkRange.from;
      output.networkTo = this.form.value.networkRange.to;
    }

    this.dialog.emit(this, output);
  }

  private createForm(): void {
    this.form = this.conditionalFormGeneratorService.generate({
      foreground: {
        value: false,
      },
      foregroundRange: {
        value: null,
        conditions: ['.foreground:false:disable'],
        validators: CustomValidators.dateRangeRequired(),
      },
      mobile: {
        value: false,
      },
      mobileRange: {
        value: null,
        conditions: ['.mobile:false:disable'],
        validators: CustomValidators.dateRangeRequired(),
      },
      network: {
        value: false,
      },
      networkRange: {
        value: null,
        conditions: ['.network:false:disable'],
        validators: CustomValidators.dateRangeRequired(),
      },
      storage: {
        value: false,
      },
    });

    this.form.setValidators([
      CustomValidators.atLeastOne(
        Validators.requiredTrue,
        [
          REPORT_TYPE.FOREGROUND,
          REPORT_TYPE.MOBILE,
          REPORT_TYPE.NETWORK,
          REPORT_TYPE.STORAGE,
        ]
      ),
    ]);
  }

  private getMinDateString(configuration: any): string | null {
    if (configuration?.items?.length) {
      const sortedItems = configuration.items.sort((a, b) => {
        if (a.activatedAt === null) {
          return 1;
        }

        if (b.activatedAt === null) {
          return -1;
        }

        return new Date(a.activatedAt).getTime() - new Date(b.activatedAt).getTime();
      });

      return sortedItems[0].activatedAt;
    }

    return null;
  }
}
